/**
 * Auth store (BUILD_SPEC.md §7.5, §19).
 *
 * Holds the current access token and the cached user profile in MEMORY
 * only — the spec is explicit (§7.5): "Access token stored in memory
 * (Zustand store), never localStorage." Persisting it to disk would
 * defeat the XSS-mitigation rationale that drove the choice in the
 * first place.
 *
 * The refresh-token cookie is httpOnly and lives in the browser's
 * cookie jar; it is invisible to JavaScript by design and is sent
 * automatically on requests to /api/auth via the proxy.
 *
 * State transitions happen in three places:
 *   • login()   — set after a successful POST /api/auth/login
 *   • setAccessToken — used by the refresh interceptor when it pulls
 *                      a fresh token from POST /api/auth/refresh
 *   • clear()   — on logout or when refresh definitively fails
 */

import { create } from "zustand";

// v1.1 B3: DISPLAY (TV-kiosk) role joined ADMIN + EMPLOYEE. Kept in
// sync with the same union in `api/types.ts`; the latter is the wire
// contract, this duplicate exists only so the store stays decoupled
// from the API layer in early-boot init order.
export type Role = "ADMIN" | "EMPLOYEE" | "DISPLAY";

export interface AuthUser {
  id: string;
  username: string;
  role: Role;
  canEdit: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  setSession: (input: { accessToken: string; user: AuthUser }) => void;
  setAccessToken: (token: string) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  user: null,
  setSession: ({ accessToken, user }) => set({ accessToken, user }),
  setAccessToken: (token) => set({ accessToken: token }),
  clear: () => set({ accessToken: null, user: null }),
}));

/** Imperative helper: outside-React access (e.g. axios interceptors). */
export const authStore = {
  getToken: () => useAuthStore.getState().accessToken,
  setToken: (token: string) => useAuthStore.getState().setAccessToken(token),
  clear: () => useAuthStore.getState().clear(),
};
